import React, { useCallback, useMemo, useState } from 'react';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    selectHasFetchedFeatureFlags,
    selectIsFlipgiveWalletBannerDisabledWeb,
} from 'common/features/store/duck/ffs';
import { getFlipgiveMigrationBannerState } from 'common/features/store/utils/flipgiveMigrationBanner';
import { useRootSelector } from 'rootStore';
import RemoteConfigWeb from 'config/RemoteConfigWeb';

export const FLIPGIVE_WALLET_BANNER_DISMISSED_KEY = 'flipgive-wallet-banner-dismissed';

const readDismissed = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        return window.localStorage.getItem(FLIPGIVE_WALLET_BANNER_DISMISSED_KEY) === 'true';
    } catch {
        return false;
    }
};

const persistDismissed = () => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(FLIPGIVE_WALLET_BANNER_DISMISSED_KEY, 'true');
    } catch {
        /* storage unavailable — banner already hidden in-memory */
    }
};

export const FlipgiveWalletBanner: React.FC = () => {
    const hasFetchedFFs = useRootSelector(selectHasFetchedFeatureFlags);
    const isBannerDisabled = useRootSelector(selectIsFlipgiveWalletBannerDisabledWeb);

    const [hasDismissed, setHasDismissed] = useState<boolean>(readDismissed);

    const { show, text } = useMemo(
        () =>
            getFlipgiveMigrationBannerState({
                hasFetchedFFs,
                isBannerDisabled,
                rawMigrationDate:
                    RemoteConfigWeb.getRemoteValue('flipgive_migration_date').asString(),
                destination: 'wallet',
            }),
        [hasFetchedFFs, isBannerDisabled]
    );

    const onDismiss = useCallback(() => {
        setHasDismissed(true);
        persistDismissed();
    }, []);

    if (!show || hasDismissed) {
        return null;
    }

    return (
        <div
            role="alert"
            aria-label={text}
            className="flex items-center gap-3 rounded border border-warning-orange-border bg-warning-orange-bg px-4 py-3 mt-2 mb-8 lg:mt-5 lg:mb-2.5 text-warning-orange"
        >
            <FontAwesomeIcon icon={faInfoCircle} size="lg" className="shrink-0" />
            <p className="text-sm font-semibold m-0">
                {text}{' '}
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-sm font-semibold no-underline bg-transparent border-0 p-0 text-brand align-baseline"
                    aria-label="Dismiss banner"
                >
                    Dismiss
                </button>
            </p>
        </div>
    );
};

FlipgiveWalletBanner.displayName = 'FlipgiveWalletBanner';
